import { useId, useMemo, useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { Badge } from '../../atoms/Badge';
import type { BadgeTone } from '../../atoms/Badge/Badge.types';
import { Button } from '../../atoms/Button';
import { Card } from '../../atoms/Card';
import { EmptyState } from '../../atoms/EmptyState';
import { Icon } from '../../atoms/Icon';
import { Inline } from '../../atoms/Inline';
import { Input } from '../../atoms/Input/Input';
import { Radio } from '../../atoms/Radio';
import { Select } from '../../atoms/Select/Select';
import { Stack } from '../../atoms/Stack';
import { Heading, Text } from '../../atoms/Typography';
import styles from './IdentifyResultsStep.module.css';
import { Alert } from '../../atoms/Alert';
import { LiveRegion } from '../../atoms/LiveRegion';
import { composeScientificName } from './scientificName';

export type ConfidenceLevel = 'high' | 'medium' | 'low';

export interface IdentifyCandidate {
  id: string;
  category: string;
  taxonomy: {
    family?: string;
    genus?: string;
    species?: string;
  };
  commonNames: string[];
  confidence: ConfidenceLevel;
  featureExplanation: string;
}

/** The user's own name for the animal when none of the suggestions is right. */
export interface OwnIdentification {
  commonName: string;
  scientificName: string;
  category: 'coral' | 'fish' | 'invertebrate' | 'other';
}

/** Selection id of the "Different animal — name it yourself" option. */
export const OWN_NAME_OPTION_ID = '__own-name__';

export interface IdentifyResultsStepProps {
  candidates: IdentifyCandidate[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onAccept: (id: string) => void;
  /** Continue with the user's own name instead of a suggestion. */
  onAcceptOwn?: (own: OwnIdentification) => void;
  /** "No match — close": closes the flow without creating anything. */
  onNoMatch: () => void;
  onRetry: () => void;
  /** Cancel while analysing or after an error; defaults to onNoMatch (both create nothing). */
  onCancel?: () => void;
  isLoading: boolean;
  error: string | null;
}

/** Secondary cue only — the confidence is always also written out as text. */
const CONFIDENCE_TONE: Record<ConfidenceLevel, BadgeTone> = {
  high: 'success',
  medium: 'warning',
  low: 'danger',
};

const CONFIDENCE_RANK: Record<ConfidenceLevel, number> = { high: 0, medium: 1, low: 2 };

const CATEGORIES: OwnIdentification['category'][] = ['coral', 'fish', 'invertebrate', 'other'];

/** Scientific name only to the depth the AI recognised: "Genus species", "Genus sp." or the family. */
export function taxonomySummary(taxonomy: IdentifyCandidate['taxonomy']): string {
  // Never "Zebrasoma Zebrasoma flavescens" when `species` already holds the binomial.
  return composeScientificName(taxonomy);
}

/** The category of the most confident suggestion, as a starting point for the own name. */
function suggestedCategory(candidates: IdentifyCandidate[]): OwnIdentification['category'] | '' {
  const top = [...candidates].sort((a, b) => CONFIDENCE_RANK[a.confidence] - CONFIDENCE_RANK[b.confidence])[0];
  const category = top?.category;
  return (CATEGORIES as string[]).includes(category ?? '') ? (category as OwnIdentification['category']) : '';
}

/**
 * Step 2 of "Identify from photo" (epic #1459). Rendered inside the flow's dialog, whose
 * title names the step (so there is no second heading here): the AI's candidates ordered
 * by confidence as a radio group, plus a last option to name the animal yourself (e.g. a
 * trade name such as "Orange Acropora"). "No match — close" is always available. Raw error
 * text is never shown.
 */
export function IdentifyResultsStep({
  candidates,
  selectedId,
  onSelect,
  onAccept,
  onAcceptOwn,
  onNoMatch,
  onRetry,
  onCancel = onNoMatch,
  isLoading,
  error,
}: IdentifyResultsStepProps) {
  const { t } = useTranslation();
  const chooseLabelId = useId();

  const [own, setOwn] = useState<{ commonName: string; scientificName: string; category: OwnIdentification['category'] | '' }>(
    () => ({ commonName: '', scientificName: '', category: suggestedCategory(candidates) }),
  );
  const [ownErrors, setOwnErrors] = useState<{ commonName?: string; category?: string }>({});
  // With no suggestions the own-name fields open on request.
  const [ownOpenWithoutCandidates, setOwnOpenWithoutCandidates] = useState(false);

  // Array.prototype.sort is stable, so equal confidence keeps the AI's order.
  const sorted = useMemo(
    () => [...candidates].sort((a, b) => CONFIDENCE_RANK[a.confidence] - CONFIDENCE_RANK[b.confidence]),
    [candidates],
  );

  const canNameOwn = Boolean(onAcceptOwn);
  const ownSelected = canNameOwn && (selectedId === OWN_NAME_OPTION_ID || (sorted.length === 0 && ownOpenWithoutCandidates));

  const confidenceBadge = (confidence: ConfidenceLevel) => (
    <Badge tone={CONFIDENCE_TONE[confidence]}>{t(`identify.results.confidence.${confidence}`)}</Badge>
  );

  const candidateDetails = (candidate: IdentifyCandidate) => {
    const summary = taxonomySummary(candidate.taxonomy);
    return (
      <>
        {summary && (
          <Text color="muted">{t('identify.results.candidate.taxonomy', { taxonomySummary: summary })}</Text>
        )}
        {candidate.featureExplanation && (
          <Text>{t('identify.results.candidate.explanation', { featureExplanation: candidate.featureExplanation })}</Text>
        )}
      </>
    );
  };

  const commonNameOf = (candidate: IdentifyCandidate) =>
    t('identify.results.candidate.commonName', {
      commonName: candidate.commonNames[0] ?? taxonomySummary(candidate.taxonomy),
    });

  const updateOwn = (field: 'commonName' | 'scientificName' | 'category', value: string) => {
    setOwn((prev) => ({ ...prev, [field]: value }));
    if (field !== 'scientificName') setOwnErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const acceptOwn = () => {
    const errors: { commonName?: string; category?: string } = {};
    if (!own.commonName.trim()) errors.commonName = t('livestock.validation.commonNameRequired');
    if (!own.category) errors.category = t('livestock.validation.categoryRequired');
    setOwnErrors(errors);
    if (Object.keys(errors).length > 0 || !onAcceptOwn) return;
    onAcceptOwn({
      commonName: own.commonName.trim(),
      scientificName: own.scientificName.trim(),
      category: own.category as OwnIdentification['category'],
    });
  };

  const handleAccept = () => {
    if (ownSelected) acceptOwn();
    else if (selectedId) onAccept(selectedId);
  };

  const ownFields = (
    <Stack gap="3">
      <Input
        id="identify-own-common-name"
        label={t('livestock.form.commonName')}
        value={own.commonName}
        onChange={(e) => updateOwn('commonName', e.target.value)}
        placeholder={t('identify.results.ownName.commonNamePlaceholder')}
        helperText={t('identify.results.ownName.hint')}
        error={ownErrors.commonName}
        required
      />
      <Input
        id="identify-own-scientific-name"
        label={t('identify.results.ownName.scientificNameOptional')}
        value={own.scientificName}
        onChange={(e) => updateOwn('scientificName', e.target.value)}
        placeholder={t('livestock.form.scientificNamePlaceholder')}
      />
      <Select
        id="identify-own-category"
        label={t('livestock.form.category')}
        value={own.category}
        onChange={(e) => updateOwn('category', e.target.value)}
        error={ownErrors.category}
        required
      >
        <option value="">{t('livestock.form.categoryPlaceholder')}</option>
        {CATEGORIES.map((category) => (
          <option key={category} value={category}>
            {t(`livestock.categories.${category}`)}
          </option>
        ))}
      </Select>
    </Stack>
  );

  const actions = (
    <Inline gap="2" wrap>
      <Button variant="ghost" onClick={onNoMatch}>
        {t('identify.results.noMatch')}
      </Button>
      <Button variant="primary" onClick={handleAccept} disabled={!ownSelected && !selectedId}>
        {t('identify.results.accept')}
      </Button>
    </Inline>
  );

  let body;
  if (isLoading) {
    body = (
      <>
        <LiveRegion>
          <Stack gap="2">
            <Text>{t('identify.results.analysing')}</Text>
            <Inline gap="2">
              <Badge tone="info">{t('identify.capture.pleaseWait')}</Badge>
            </Inline>
          </Stack>
        </LiveRegion>
        <Inline gap="2" wrap>
          <Button variant="ghost" onClick={onCancel}>
            {t('identify.results.cancel')}
          </Button>
        </Inline>
      </>
    );
  } else if (error) {
    body = (
      <>
        <Alert title={t('identify.results.error.title')} titleLevel={3}>
          {t('identify.results.error.body')}
        </Alert>
        <Inline gap="2" wrap>
          <Button variant="secondary" onClick={onRetry}>
            {t('identify.results.error.retry')}
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            {t('identify.results.cancel')}
          </Button>
        </Inline>
      </>
    );
  } else if (sorted.length === 0) {
    // EmptyState is itself a role="status" region, so the transition is announced.
    body = (
      <>
        <EmptyState
          icon={<Icon name="search-off" />}
          headline={t('identify.results.noCandidates.title')}
          description={t('identify.results.noCandidates.body')}
          actionLabel={t('identify.results.noMatch')}
          actionVariant="primary"
          onAction={onNoMatch}
          headingLevel={3}
        />
        {canNameOwn && !ownOpenWithoutCandidates && (
          <Inline gap="2" wrap>
            <Button variant="secondary" onClick={() => setOwnOpenWithoutCandidates(true)}>
              {t('identify.results.ownName.start')}
            </Button>
          </Inline>
        )}
        {ownSelected && (
          <Card className={styles.candidateCard}>
            <Stack gap="3">
              <Heading level={3}>{t('identify.results.ownName.option')}</Heading>
              {ownFields}
            </Stack>
          </Card>
        )}
        {ownSelected && (
          <Inline gap="2" wrap>
            <Button variant="primary" onClick={acceptOwn}>
              {t('identify.results.accept')}
            </Button>
          </Inline>
        )}
      </>
    );
  } else {
    body = (
      <>
        <Text color="muted">{t('identify.results.instructions')}</Text>
        <LiveRegion>
          <Text color="muted">{t('identify.results.loaded', { count: sorted.length })}</Text>
        </LiveRegion>
        <fieldset className={styles.fieldset} role="radiogroup" aria-labelledby={chooseLabelId}>
          <Stack gap="3">
            <Heading level={3} id={chooseLabelId}>
              {t('identify.results.chooseOne')}
            </Heading>
            {sorted.map((candidate) => (
              <Card key={candidate.id} className={styles.candidateCard}>
                <Stack gap="2">
                  <Inline gap="2" wrap align="center">
                    <Radio
                      name="identify-candidate"
                      value={candidate.id}
                      label={commonNameOf(candidate)}
                      checked={selectedId === candidate.id}
                      onChange={() => onSelect(candidate.id)}
                    />
                    {confidenceBadge(candidate.confidence)}
                  </Inline>
                  {candidateDetails(candidate)}
                </Stack>
              </Card>
            ))}
            {canNameOwn && (
              <Card className={styles.candidateCard}>
                <Radio
                  name="identify-candidate"
                  value={OWN_NAME_OPTION_ID}
                  label={t('identify.results.ownName.option')}
                  checked={selectedId === OWN_NAME_OPTION_ID}
                  onChange={() => onSelect(OWN_NAME_OPTION_ID)}
                />
              </Card>
            )}
          </Stack>
        </fieldset>
        {/* Outside the radiogroup: a radiogroup may only contain radios. */}
        {ownSelected && <Card className={styles.candidateCard}>{ownFields}</Card>}
        {actions}
      </>
    );
  }

  return (
    <Stack gap="4" className={styles.container}>
      {body}
    </Stack>
  );
}
